import { useEffect, useState } from "react";
import { Loader2, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface Props {
  email: string | null;
  userId: string | null;
}

type ModelChoice = "lite" | "standard" | "pro" | "muse";

const OPTIONS: { id: ModelChoice; label: string; hint: string }[] = [
  { id: "lite", label: "Nano Banana 2 Lite", hint: "Default · fastest, lowest cost" },
  { id: "standard", label: "Nano Banana 2", hint: "Standard quality" },
  { id: "pro", label: "Nano Banana 2 Pro", hint: "Highest quality, slowest" },
  { id: "muse", label: "Meta Muse Image", hint: "Experimental · designs and refines, no fallback" },
];

const flagsFor = (choice: ModelChoice) => ({
  use_pro_image_model: choice === "pro",
  use_lite_image_model: choice === "lite",
  use_muse_image_model: choice === "muse",
});

const HueloTeamSettings = ({ email, userId }: Props) => {
  const { toast } = useToast();
  const isTeam = !!email && email.toLowerCase().endsWith("@huelo.co");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [choice, setChoice] = useState<ModelChoice>("lite");

  useEffect(() => {
    if (!isTeam || !userId) {
      setLoading(false);
      return;
    }
    (async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("use_pro_image_model, use_lite_image_model, use_muse_image_model")
        .eq("user_id", userId)
        .maybeSingle();
      if (!error && data) {
        const row = data as { use_pro_image_model?: boolean; use_lite_image_model?: boolean; use_muse_image_model?: boolean };
        setChoice(row.use_muse_image_model ? "muse" : row.use_pro_image_model ? "pro" : row.use_lite_image_model ? "lite" : "standard");
      }
      setLoading(false);
    })();
  }, [isTeam, userId]);

  if (!isTeam) return null;

  const handleSelect = async (next: ModelChoice) => {
    if (!userId || next === choice || saving) return;
    const prev = choice;
    setChoice(next);
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update(flagsFor(next) as never)
      .eq("user_id", userId);
    setSaving(false);
    if (error) {
      setChoice(prev);
      toast({ title: "Couldn't update setting", description: error.message, variant: "destructive" });
      return;
    }
    toast({
      title: `${OPTIONS.find((o) => o.id === next)?.label} selected`,
      description: "Final room renders will use this image model.",
    });
  };

  return (
    <div className="bg-card border border-border p-6 w-full space-y-4">
      <div>
        <h3 className="text-foreground font-medium">Huelo team · Image model</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Override the image model used for final room renders. Analysis, planning, and
          evaluation models are unchanged. Team-only setting.
        </p>
      </div>

      <div className="space-y-2">
        {OPTIONS.map((opt) => {
          const active = choice === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              disabled={loading || saving}
              onClick={() => handleSelect(opt.id)}
              className={cn(
                "w-full flex items-center justify-between gap-3 border p-3 text-left transition-colors",
                active
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-foreground/30",
                (loading || saving) && "opacity-60",
              )}
            >
              <div className="min-w-0">
                <div className="text-sm text-foreground">{opt.label}</div>
                <div className="text-xs text-muted-foreground">{opt.hint}</div>
              </div>
              {loading || (saving && active) ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
              ) : active ? (
                <Check className="h-4 w-4 text-primary shrink-0" />
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default HueloTeamSettings;
